import React, { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Namespaces } from 'types/locales';
import ProvinceCitySeoLinks from './index';
import type { LocalizedSeoLinkData, SeoLinkData } from 'data/seo-links';

export interface LocalizedProvinceCitySeoLinksProps {
  seoLinks: LocalizedSeoLinkData;
}

/**
 * LocalizedProvinceCitySeoLinks component that translates SEO links on the client side.
 * 
 * This component takes LocalizedSeoLinkData (which contains translation keys and parameters)
 * and translates them using the common namespace before rendering.
 * 
 * Benefits:
 * - Supports multiple languages through i18next
 * - Translation keys are type-safe (validated at build time)
 * - Separates data generation (server) from presentation (client)
 * - Maintains backward compatibility with fallback strings
 * 
 * @example
 * ```tsx
 * const seoLinks = await seoLinksApi.getLocalizedCityLinks(...);
 * return <LocalizedProvinceCitySeoLinks seoLinks={seoLinks} />;
 * ```
 */
export default function LocalizedProvinceCitySeoLinks({
  seoLinks,
}: LocalizedProvinceCitySeoLinksProps) {
  const { t } = useTranslation(Namespaces.COMMON);

  // Transform LocalizedSeoLinkData to SeoLinkData by applying translations
  const translatedData: SeoLinkData = useMemo(() => {
    return {
      title: seoLinks.titleKey
        ? t(seoLinks.titleKey, {
          ...seoLinks.titleParams,
          defaultValue: seoLinks.title || '',
        })
        : seoLinks.title || '',
      links: seoLinks.links.map(link => ({
        name: link.nameKey
          ? t(link.nameKey, {
            ...link.nameParams,
            defaultValue: link.name || '',
          })
          : link.name || '',
        link: link.link,
        label: link.label,
      })),
    };
  }, [seoLinks, t]);

  return <ProvinceCitySeoLinks seoLinks={translatedData} />;
}
